// import React  from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useApi } from '../utils/useApi'; // auto-refreshing fetch wrapper
import { toDE } from '../utils/utils'
import Stack from 'react-bootstrap/Stack';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import ToggleOff from '../components/icons/ToggleOff';
import ToggleOn from '../components/icons/ToggleOn';
import '../styles/documents.css';
import TaskModal from "../components/tasks/modals/TaskModal";
import TasksPanel from '../components/tasks/TasksPanel';
import { useParams, useNavigate } from 'react-router-dom';
import LinkedDecisionsList from '../components/decisions/LinkedDecisionsList';
import { useAuth } from '../contexts/AuthContext'

type ClusterItem = {
  name: string;
  color: string | null;
}

type DocumentEntry = {
  id: string;
  law_id: number;
  law_name: string;
  law_part_id: number;
  abbr: string;
  last_change_date: string;
  last_change: string;
  paragraphs: string;
  regulation: string;
  comments: string;
  amendment_date: string;
  amendment_comment: string;
  notes: string;
  clusters: ClusterItem[];
  legal_area: string;
  legal_area_color: string;
  link1: string;
  link2: string;
  client_law_part_id: number;
  number_of_tasks: number;
  number_of_decisions: number;
  tasks?: any[]; // associated tasks
};

// type ExportDocument = {
//   law_name: string;
//   abbr: string;
// }

type SortableKey = Exclude<keyof DocumentEntry, 'clusters'> | 'cluster_text';

// type ApiMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

const DocumentList = () => {
  const { law_part_id } = useParams<{ law_part_id?: string }>();
  const { apiFetch } = useApi();
  const auth = useAuth();
  const [documents, setDocuments] = useState<DocumentEntry[]>([]);
  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortableKey>('law_name');
  const [sortAsc, setSortAsc] = useState(true);
  const [maxhOn, setMaxhOn] = useState(true);
  const [selectedClusterKeys, setSelectedClusterKeys] = useState<string[]>([]);
  const [selectedLegalAreaKeys, setSelectedLegalAreaKeys] = useState<string[]>([]);
  const [showClusterModal, setShowClusterModal] = useState(false);
  const [showLegalAreaModal, setShowLegalAreaModal] = useState(false);
  // const [tasks, setTasks] = useState<any[]>([]);
  const [activeTask, setActiveTask] = useState<any | null>(null);
  // const [clientLawPartId, setClientLawPartId] = useState<number | null>(null);
  const toggleMaxh = () => setMaxhOn(prev => !prev);
  const navigate = useNavigate();
  const [clientFlags, setClientFlags] = useState<{ show_tasks: boolean; show_decisions: boolean; show_colors: boolean; } | null>(null);

  useEffect(() => {
    apiFetch('/api/documents')
      .then(setDocuments)
      .catch(err => console.error('Dokumente konnten nicht geladen werden', err));
    apiFetch('/api/clients/flags')
      .then(data => setClientFlags({ show_tasks: data.show_tasks, show_decisions: data.show_decisions, show_colors: data.show_colors  }))
      .catch(err => console.error('Client info konnte nicht geladen werden', err));
  }, [apiFetch, auth]);

  useEffect(() => {
    if (law_part_id) {
      const doc = documents.find(d => d.law_part_id === parseInt(law_part_id));
      if (doc) {
        setSelectedDoc(doc);
        setShowModal(true);
      }
    }
  }, [law_part_id, documents]);

const normalize = (s?: string | null) => (s ?? '').toString().trim();
  // Derive a plain text for clusters to use in filter/sort
  const clusterText = (doc: DocumentEntry) =>
    (doc.clusters ?? []).map(c => normalize(c.name)).filter(Boolean).join(', ');

  const legalAreaOptions = useMemo(() => {
    const map = new Map<string, { key: string; name: string; color: string | null }>();

    (documents ?? []).forEach((doc: DocumentEntry) => {
      const name = normalize(doc.legal_area);
      if (!name) return;
      const key = name.toLowerCase();
      if (!map.has(key)) {
        map.set(key, { key, name, color: doc.legal_area_color ?? null });
      }
    });

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [documents]);

  const clusterOptions = useMemo(() => {
    const map = new Map<string, { key: string; name: string; color: string | null }>();

    (documents ?? []).forEach((doc: DocumentEntry) => {
      (doc.clusters ?? []).forEach((cluster: ClusterItem) => {
        const name = normalize(cluster.name);
        if (!name) return;
        const key = name.toLowerCase();
        if (!map.has(key)) {
          map.set(key, { key, name, color: cluster.color ?? null });
        }
      });
    });

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [documents]);

  const filtered = documents
  .filter(doc => {
      const q = filter.toLowerCase();
      const matchesText =
        normalize(doc.law_name).toLowerCase().includes(q) ||
        normalize(doc.abbr).toLowerCase().includes(q) ||
        normalize(doc.regulation).toLowerCase().includes(q) ||
        normalize(doc.legal_area).toLowerCase().includes(q) ||
        clusterText(doc).toLowerCase().includes(q);

      const matchesClusters = selectedClusterKeys.length === 0 ||
        (doc.clusters?.some((c: any) => selectedClusterKeys.includes(normalize(c.name).toLowerCase())) ?? false);

      const matchesLegalArea = selectedLegalAreaKeys.length === 0 ||
        selectedLegalAreaKeys.includes(normalize(doc.legal_area).toLowerCase());

      return matchesText && matchesClusters && matchesLegalArea;
    })
  .sort((a, b) => {
    const aVal =
      sortKey === 'cluster_text'
        ? clusterText(a).toLowerCase()
        : normalize((a as any)[sortKey]).toLowerCase();
    const bVal =
      sortKey === 'cluster_text'
        ? clusterText(b).toLowerCase()
        : normalize((b as any)[sortKey]).toLowerCase();
    return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });

  const [selectedDoc, setSelectedDoc] = useState<DocumentEntry | null>(null);
  const [showModal, setShowModal] = useState(false);

  const [editNotes, setEditNotes] = useState<string>('');

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedDoc) return;
    // console.log('Selected doc changed:', selectedDoc);
    setEditNotes(selectedDoc.notes ?? '');
    setErrorMsg(null);
  }, [selectedDoc]);

  const emptyToNull = (s: string) => (s.trim() === '' ? null : s);

  const isDirty =
    selectedDoc &&
    ((editNotes ?? '') !== (selectedDoc.notes ?? ''));

  const handleRowClick = (doc: DocumentEntry) => {
    navigate(`/documents/${doc.law_part_id}`);
  };

  const latestChangeLaw = (changes: string) => {
    const first = changes.split('\n')[0];
    return first;
  };

  const otherChangeLaws = (changes: string) => {
    const rest = changes.split('\n').slice(1).join('\n');
    return rest;
  };

  const doShowColors = () => {
    return clientFlags?.show_colors ?? true;
  };

  const docColor = (doc: DocumentEntry) => {
    var docColor = doc.legal_area_color ?? "#ffffff";
    if (!doShowColors()) { docColor = "#ffffff"; }
    return docColor;
  }

  const docStyle = (doc: DocumentEntry) => {
    return { backgroundColor: docColor(doc) };
  }

  const exportExcel = async () => {
    try {
      apiFetch('/api/documents/export', 'GET', null, false, 'blob')
        .then((excel_doc) => {
          console.log('Received Excel document for export:', excel_doc);
          const url = window.URL.createObjectURL(excel_doc);

          const a = document.createElement('a');
          a.href = url;
          const current_datetime = new Date().toISOString().split('T')[0];
          a.download = `documents_audit_export_${current_datetime}.xlsx`;
          document.body.appendChild(a);
          a.click();
          a.remove();

          window.URL.revokeObjectURL(url);
        }
      )
      .catch(err => console.error('Dokumente konnten nicht für den Export geladen werden', err));
    } catch (err) {
      console.error('Excel export failed', err);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDoc(null);
    // Reload document tasks after saving
    apiFetch('/api/documents')
      .then((docs: DocumentEntry[]) => {
        const updatedDoc = docs.find(d => d.client_law_part_id === selectedDoc?.client_law_part_id);
        if (updatedDoc) {
          setSelectedDoc(updatedDoc);
          setDocuments(prev =>
            prev.map(d =>
              d.client_law_part_id === updatedDoc.client_law_part_id
                ? updatedDoc
                : d
            )
          );
          }
      })
      .catch(err => console.error('Dokumente konnten nicht geladen werden', err));
  };

  const saveEdits = async () => {
    if (!selectedDoc) return;
    try {
      setSaving(true);
      setErrorMsg(null);

      const payload = {
        notes: emptyToNull(editNotes),
      };

      // apiFetch signature you use elsewhere: apiFetch(url, method?, body?)
      const updated = await apiFetch(`/api/documents/${selectedDoc.client_law_part_id}`, 'PATCH', payload);

      // Optimistically update local list
      setDocuments(prev =>
        prev.map(d =>
          d.client_law_part_id === selectedDoc.client_law_part_id
            ? { ...d, notes: updated?.notes ?? payload.notes ?? '' }
            : d
        )
      );

      // Also update currently selectedDoc so the modal shows saved state
      setSelectedDoc(s => (s ? { ...s, notes: payload.notes ?? '' } : s));
    } catch (err: any) {
      console.error('Failed saving comments/notes', err);
      setErrorMsg(err?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isSave = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's';
      if (showModal && isSave) {
        e.preventDefault();
        if (isDirty && !saving) saveEdits();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showModal, isDirty, saving, saveEdits]);

  const handleSort = (key: SortableKey) => {
    if (sortKey === key) setSortAsc(prev => !prev);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const closeDocumentModal = () => {
    setShowModal(false);
    setSelectedDoc(null);
    navigate('/documents');
  };

  useEffect(() => {
    if (documents.length === 0 || !law_part_id) return;
    // If we have documents, check if URL has a law_part_id that matches
    const doc = documents.find(d => d.law_part_id === Number(law_part_id));
    if (doc) {
      setSelectedDoc(doc);
      setShowModal(true);
    }
  }, [law_part_id, documents]);

  function LawNameItem({law_name}: { law_name: string }) {
    return <>{law_name}</>
  };

  function LawAbbrItem({ law_abbr }: { law_abbr?: string }) {
    if (!law_abbr || law_abbr.trim() == "") return <></>;
    return <><i>({law_abbr})</i></>;
  };

  function LawAbbrItemNL({ law_abbr }: { law_abbr: string }) {
    if (!law_abbr || law_abbr.trim() == "") return <></>;
    return <><br /><LawAbbrItem law_abbr={law_abbr} /></>;
  };

  function ParagraphItemNL({ paragrafs }: { paragrafs: string }) {
    if (!paragrafs || paragrafs.trim() == "") return <></>;
    return <><br /><span className='divider'>{paragrafs}</span></>;
  };

{/* 
//#region HTML
*/}
  return (
    <>
    <div className="container mt-4">
      <Stack direction="horizontal" gap={3} className="mb-3">
        <h2 className="mb-0">Rechts Informations System</h2>
        <Button variant="outline-secondary" size="sm" onClick={exportExcel}>Excel Export</Button>
      </Stack>
      <Stack direction="horizontal" gap={0}>
        <Form.Control
          type="search"
          placeholder="Filtern nach Vorschrift, Abkürzung, Rechtsgebiet, Cluster, Inhalt"
          className="p-2"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <Button
          variant={selectedClusterKeys.length > 0 ? 'primary' : 'outline-secondary'}
          size="sm"
          className="p-2"
          onClick={() => setShowClusterModal(true)}
          title="Cluster auswählen"
        >
          Cluster {selectedClusterKeys.length > 0 && `(${selectedClusterKeys.length})`}
        </Button>
        <Button
          variant={selectedLegalAreaKeys.length > 0 ? 'primary' : 'outline-secondary'}
          size="sm"
          className="p-2"
          onClick={() => setShowLegalAreaModal(true)}
          title="Rechtsgebiete auswählen"
        >
          Rechtsgebiete {selectedLegalAreaKeys.length > 0 && `(${selectedLegalAreaKeys.length})`}
        </Button>
        <div className="p-2">
          <span className='toggle-btn' onClick={toggleMaxh} title={maxhOn ? 'Maximale Höhe deaktivieren' : 'Maximale Höhe aktivieren'}>
            {maxhOn ? <ToggleOn /> : <ToggleOff />}
          </span>
        </div>
      </Stack>

      <div className='table-scroll-wrapper'>
        <Table bordered hover responsive className="fixed-header-table doc-table">
          <thead>
            <tr>
              <th>
                <span onClick={() => handleSort('law_name')} style={{ cursor: 'pointer' }}>
                  Vorschrift&nbsp;{sortKey === 'law_name' && (sortAsc ? '↑' : '↓')}
                </span>
                <br />
                <span onClick={() => handleSort('abbr')} style={{ cursor: 'pointer' }}>
                  <i>(Abkürzung Vorschrift)</i>&nbsp;{sortKey === 'abbr' && (sortAsc ? '↑' : '↓')}
                </span>
                <br />
                <span className='divider'>
                  Paragraf
                </span>
              </th>
              <th>
                <span onClick={() => handleSort('legal_area')} style={{ cursor: 'pointer' }}>
                  Rechtsgebiet&nbsp;{sortKey === 'legal_area' && (sortAsc ? '↑' : '↓')}
                </span>
                <br />
                <span onClick={() => handleSort('cluster_text')} style={{ cursor: 'pointer' }}>
                  Cluster&nbsp;{sortKey === 'cluster_text' && (sortAsc ? '↑' : '↓')}
                </span>
              </th>
              {/* <th>Paragraf</th> */}
              <th>Inhalt</th>
              <th onClick={() => handleSort('last_change_date')} style={{ cursor: 'pointer' }}>
                Datum letzte Änderung&nbsp;{sortKey === 'last_change_date' && (sortAsc ? '↑' : '↓')}
              </th>
              {clientFlags?.show_tasks && (
                <th onClick={() => handleSort('number_of_tasks')} style={{ cursor: 'pointer' }}>
                  #&nbsp;Maßn.&nbsp;{sortKey === 'number_of_tasks' && (sortAsc ? '↑' : '↓')}
                </th>
              )}
              {clientFlags?.show_decisions && (
                <th onClick={() => handleSort('number_of_decisions')} style={{ cursor: 'pointer' }}>
                  #&nbsp;Besch.&nbsp;{sortKey === 'number_of_decisions' && (sortAsc ? '↑' : '↓')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.map((doc) => (
              <tr
                key={`${doc.id}-${doc.paragraphs}`}
                onClick={() => handleRowClick(doc)}
                style={docStyle(doc)}
              >
                <td style={docStyle(doc)}>
                  <LawNameItem law_name={doc.law_name} />
                  <LawAbbrItemNL law_abbr={doc.abbr} />
                  <ParagraphItemNL paragrafs={doc.paragraphs} />
                </td>

                <td style={docStyle(doc)}>
                  <div className="legal-area-doc" style={docStyle(doc)}>
                    {doc.legal_area}
                  </div>

                  {/* Render multiple clusters */}
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {doc.clusters?.map((c, idx) => (
                      <div
                        key={`${doc.id}-cluster-${idx}-${c.name}`}
                        className="cluster"
                        style={{ backgroundColor: c.color ?? undefined }}
                        onClick={(e) => { e.stopPropagation(); /* (optional) prevent opening modal when clicking tag */ }}
                      >
                        {c.name}
                      </div>
                    ))}
                  </div>
                </td>

                {/* <td style={docStyle(doc)}>
                  <div className={maxhOn ? 'maxh' : 'maxh-off'}>{doc.paragraphs}</div>
                </td> */}
                <td style={docStyle(doc)}>
                  <div className={maxhOn ? 'maxh' : 'maxh-off'}>{doc.regulation}</div>
                </td>
                <td style={docStyle(doc)}>
                  {toDE(doc.last_change_date)}
                </td>
                {clientFlags?.show_tasks && (
                  <td style={docStyle(doc)} className='doc-bold-center'>
                    {doc.number_of_tasks > 0 ? doc.number_of_tasks : ''}
                  </td>
                )}
                {clientFlags?.show_decisions && (
                  <td style={docStyle(doc)} className='doc-bold-center'>
                    {doc.number_of_decisions > 0 ? doc.number_of_decisions : ''}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
{/* 
//#region DocumentModal
*/}
      <Modal show={showModal} onHide={closeDocumentModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title title={'law_id: ' + selectedDoc?.law_id}>
            {selectedDoc?.law_name} <LawAbbrItem law_abbr={selectedDoc?.abbr} />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDoc && (
            <div>
              <div className='mt-3'>
                {/* <p><strong>Law ID:</strong> {selectedDoc.law_id}</p> */}
                <p><strong>Rechtsgebiet:</strong> {selectedDoc.legal_area}</p>
                <p><strong>Cluster:</strong>{' '}
                  {selectedDoc.clusters?.length
                    ? selectedDoc.clusters.map(c => c.name).join(', ')
                    : '—'}
                </p>
                <p>
                  <strong>Vorschrift: </strong>
                  {selectedDoc.law_name}&nbsp;
                  <Button target='_blank' href={selectedDoc.link2} variant="secondary" size="sm">Link zur Gesamtvorschrift</Button>
                </p>
                <p><strong>Abkürzung Vorschrift:</strong> {selectedDoc.abbr}</p>
                <p><strong>Datum letzte Änderung:</strong> {toDE(selectedDoc.last_change_date)}</p>
                <p>
                  <strong>Änderung durch Vorschrift: </strong>
                  <Button target='_blank' href={selectedDoc.link1} variant="secondary" size="sm">{latestChangeLaw(selectedDoc.last_change)}</Button>
                </p>
                <p>
                  <strong>Andere Änderungen: </strong>
                  {otherChangeLaws(selectedDoc.last_change)}
                </p>
                {/* <p><strong>Paragraf:</strong> {selectedDoc.paragraphs}</p> */}
                <p><strong title={'law_part_id: ' + selectedDoc?.law_part_id}>Paragraf:</strong> {selectedDoc.paragraphs}</p>
                <p><strong>Inhalt:</strong> {selectedDoc.regulation}</p>
                <p><strong>Anmerkungen:</strong> {selectedDoc.comments}</p>
                <p><strong>Datum des Inkrafttretens der Änderung:</strong> {toDE(selectedDoc.amendment_date)}</p>
                <p><strong>Kurzbeschreibung der Änderung:</strong> {selectedDoc.amendment_comment}</p>

                {clientFlags?.show_decisions && (
                  <>
                    <hr />
                    <h5>Verknüpfte Bescheide</h5>
                    <LinkedDecisionsList lawPartId={selectedDoc.law_part_id} />
                  </>
                )}
                {clientFlags?.show_tasks && (
                  <>
                    <hr />
                    <TasksPanel law_part_id={selectedDoc.law_part_id} hideId={true} showRegulationColumn={false} highlightRegulationColumnOnly={false} applyRowColorFromDeadline={true} showAddButton={true} />
                  </>
                )}

                <Form.Group className="mt-3">
                  <Form.Label><strong>Notizen:</strong></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Notizen hier eingeben…"
                  />
                </Form.Group>
                {errorMsg && (
                  <div className="text-danger mt-2">
                    {errorMsg}
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeDocumentModal}
            disabled={saving}
          >
            Schließen
          </Button>
          <Button
            variant={isDirty ? 'primary' : 'outline-primary'}
            onClick={saveEdits}
            disabled={!isDirty || saving}
          >
            {saving ? 'Speichern…' : 'Speichern'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
    {activeTask && console.log("Active task for modal:", activeTask)}
    {activeTask && (
      <TaskModal
        show={true}
        TaskId={activeTask.id}
        t={activeTask}
        onClose={() => setActiveTask(null)}
        onSave={() => handleCloseModal()}
        law_part_id={activeTask.law_part_id ?? 0}
      />
    )}

    <Modal show={showClusterModal} onHide={() => setShowClusterModal(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Cluster auswählen</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group>
          {(clusterOptions ?? []).map((cluster: any) => (
            <Form.Check
              key={cluster.key}
              type="checkbox"
              id={`cluster-filter-${cluster.key}`}
              label={
                <span className="cluster" style={{ backgroundColor: cluster.color, padding: '2px 8px', borderRadius: 6 }}>
                  {cluster.name}
                </span>
              }
              checked={selectedClusterKeys.includes(cluster.key)}
              onChange={(e) => {
                setSelectedClusterKeys(prev =>
                  e.target.checked
                    ? [...prev, cluster.key]
                    : prev.filter(key => key !== cluster.key)
                );
              }}
            />
          ))}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowClusterModal(false)}>Schließen</Button>
        {selectedClusterKeys.length > 0 && (
          <Button variant="outline-secondary" onClick={() => setSelectedClusterKeys([])}>Alles löschen</Button>
        )}
      </Modal.Footer>
    </Modal>

    <Modal show={showLegalAreaModal} onHide={() => setShowLegalAreaModal(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Rechtsgebiete auswählen</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group>
          {(legalAreaOptions ?? []).map((legalArea: any) => (
            <Form.Check
              key={legalArea.key}
              type="checkbox"
              id={`legal-area-filter-${legalArea.key}`}
              label={
                <span className="legal-area" style={{ backgroundColor: legalArea.color, padding: '2px 8px', borderRadius: 6 }}>
                  {legalArea.name}
                </span>
              }
              checked={selectedLegalAreaKeys.includes(legalArea.key)}
              onChange={(e) => {
                setSelectedLegalAreaKeys(prev =>
                  e.target.checked
                    ? [...prev, legalArea.key]
                    : prev.filter(key => key !== legalArea.key)
                );
              }}
            />
          ))}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowLegalAreaModal(false)}>Schließen</Button>
        {selectedLegalAreaKeys.length > 0 && (
          <Button variant="outline-secondary" onClick={() => setSelectedLegalAreaKeys([])}>Alles löschen</Button>
        )}
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default DocumentList;

